/**
 * Fire-and-forget click reporter for the N3 "honest bell".
 *
 * Every tap surface (the in-app rows, the warm SW relay, the cold-start nref
 * boot, and the native FCM tap) reports the click to
 * `POST /api/v1/notifications/{id}/click`. The backend stamps clicked_at and
 * advances the row unread -> read in one write.
 *
 * Contract (why this hook is deliberately minimal):
 *  - NO optimistic state - the count/list refresh comes from the onSettled
 *    invalidation (and the backend's own NOTIFICATION_UPDATED SSE), not a
 *    hand-rolled cache write.
 *  - NEVER blocks or delays navigation - the returned reporter calls `mutate`
 *    (not `mutateAsync`) and returns void synchronously. Callers never await it,
 *    so a slow/failed POST cannot hold up the tap's navigation.
 *  - Silent on failure - an expired session or a 404 (wrong / another user's /
 *    hidden id) is a no-op: `mutate` captures the rejection in mutation state,
 *    no onError re-throws it, no toast fires.
 *  - Call-site sequencing: OPEN surfaces report at tap time; surfaces that pair
 *    the click with a ROW MUTATION (claim/decline/we-met) MUST report in that
 *    mutation's onSuccess. `/click` advances unread -> read server-side and the
 *    onSettled below refetches the list, so reporting before persistence would
 *    both mark a still-actionable row read on mutation failure and race the
 *    refetch against the caller's optimistic write.
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clickNotificationApiV1NotificationsNotificationIdClickPost } from '@api-client/api-client';
import { notificationCountKey, notificationsBaseKey } from '@lib/cache/notifications';

/**
 * @returns `reportClick(notificationId)` - a stable, fire-and-forget reporter.
 *   Passing a falsy id (legacy / pre-deploy pushes that predate notification_id)
 *   is a silent no-op, so callers can hand it an optional ref without a guard.
 */
export function useReportNotificationClick() {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (notificationId: string) =>
      clickNotificationApiV1NotificationsNotificationIdClickPost(notificationId),
    // Runs on success AND error: refresh the honest badge count and the list
    // rows so the read/clicked state reconciles. Fire-and-forget invalidations
    // (void) - nothing here can throw back to the caller's navigation.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationCountKey() });
      void queryClient.invalidateQueries({ queryKey: notificationsBaseKey() });
    },
  });

  // `mutate` is referentially stable across renders (RQ v5); useCallback keeps
  // the returned reporter stable so the push surfaces can hold it in a
  // useLatestRef without re-running that ref's sync effect every render.
  return useCallback(
    (notificationId: string | null | undefined): void => {
      if (!notificationId) return;
      mutate(notificationId);
    },
    [mutate],
  );
}
